import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Shield, Lock } from 'lucide-react';
import { useUpgradeModal } from '../../contexts/UpgradeModalContext';
import type { UpgradeModalVariant } from '../../contexts/UpgradeModalContext';
import { useCheckout } from '../../hooks/useCheckout';
import { PLAN_PRICES_USD, formatUsd, type CheckoutPlanId } from '../../data/pricingPlans';
import { PricingPlans } from '../Pricing/PricingPlans';
import { CheckoutAuthPrompt } from '../Pricing/CheckoutAuthPrompt';
import { PaymentNotice } from '../Payment/PaymentNotice';
interface PromptCopy {
  eyebrow: string;
  title: string;
  body: string;
  primaryLabel: string;
  primaryAction: 'free_scan' | 'single_scan' | 'invite';
}
const PROMPTS: Record<Exclude<UpgradeModalVariant, 'plans'>, PromptCopy> = {
  welcome: {
    eyebrow: 'Access granted',
    title: 'Welcome to ToxicDetector AI 🛡️',
    body: 'Start with your free forensic scan or unlock unlimited analysis.',
    primaryLabel: 'Start free scan',
    primaryAction: 'free_scan'
  },
  free_scan_complete: {
    eyebrow: 'Scan complete',
    title: 'Want deeper analysis?',
    body: `Your free scan is complete. Unlock the full forensic report for ${formatUsd(PLAN_PRICES_USD.single_scan)} or get unlimited access with a subscription.`,
    primaryLabel: `Get full report — ${formatUsd(PLAN_PRICES_USD.single_scan)}`,
    primaryAction: 'single_scan'
  },
  no_credits: {
    eyebrow: 'No credits left',
    title: "You're out of free scans",
    body: 'Share ToxicDetector with a friend and earn 5 free credits, or unlock unlimited analysis.',
    primaryLabel: 'Invite a friend',
    primaryAction: 'invite'
  }
};
export function UpgradeModal() {
  const {
    variant,
    isOpen,
    closeUpgradeModal
  } = useUpgradeModal();
  const navigate = useNavigate();
  const {
    onCheckout,
    pendingPlan,
    authPromptPlan,
    dismissAuthPrompt
  } = useCheckout('upgrade_modal');
  const [showPlans, setShowPlans] = useState(false);
  useEffect(() => {
    setShowPlans(variant === 'plans');
  }, [variant]);
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeUpgradeModal();
    };
    window.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, closeUpgradeModal]);
  const prompt = variant && variant !== 'plans' ? PROMPTS[variant] : PROMPTS.welcome;
  const goTo = (path: string) => {
    closeUpgradeModal();
    navigate(path);
  };
  const handlePrimary = () => {
    if (prompt.primaryAction === 'free_scan') return goTo('/upload');
    if (prompt.primaryAction === 'invite') return goTo('/dashboard/referrals');
    void onCheckout('single_scan');
  };
  const handleCheckout = (plan: CheckoutPlanId) => {
    void onCheckout(plan);
  };
  return <AnimatePresence data-id="element-3141" data-mp="mh55rjnl29ib">
      {isOpen && <div className="fixed inset-0 z-[100] flex items-end justify-center p-0 sm:items-center sm:p-6" data-id="element-3142" data-mp="mg3xfmxyc14a">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} onClick={closeUpgradeModal} className="absolute inset-0 bg-black/80 backdrop-blur-sm" data-id="element-3143" data-mp="rh19riwa60xv" />
          <motion.div role="dialog" aria-modal="true" aria-labelledby="upgrade-modal-title" initial={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className={`relative z-10 max-h-[92vh] w-full overflow-y-auto rounded-t-2xl border border-gray-800 bg-[#050505] p-6 sm:rounded-2xl sm:p-8 ${showPlans ? 'max-w-5xl' : 'max-w-lg'}`} data-id="element-3144" data-mp="dgqlmnmcv0ge">
            <button type="button" onClick={closeUpgradeModal} aria-label="Close" className="absolute right-4 top-4 rounded-lg p-2 text-gray-500 transition-colors duration-150 ease-out hover:bg-white/5 hover:text-white focus:outline-none focus:ring-2 focus:ring-white" data-id="element-3145" data-mp="25i63tljuyosq">
              <X size={18} data-id="element-3146" data-mp="kzpzyeuym1sw" />
            </button>

            {showPlans ? <div data-id="element-3147" data-mp="g150wirg1o61">
                <header className="mb-7 max-w-xl pr-10" data-id="element-3148" data-mp="g1ohnw5cy6ih">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF0000]" data-id="element-3149" data-mp="g39fbumyy5kk">
                    Clearance levels
                  </p>
                  <h2 id="upgrade-modal-title" className="mt-2 text-2xl font-black uppercase tracking-tight text-white" data-id="element-3150" data-mp="2wmjrfzqn4jdf">
                    Choose your access
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-gray-400" data-id="element-3151" data-mp="2mvp38ptu39pc">
                    Every plan runs the same forensic engine — pick how much
                    clarity you need.
                  </p>
                </header>
                <PaymentNotice className="mb-6" data-id="element-3152" data-mp="1z0cmupuc6r12" />
                <PricingPlans onCheckout={handleCheckout} pendingPlan={pendingPlan} freeHref="/upload" data-id="element-3153" data-mp="1z1npunfyjur4" />
              </div> : <div data-id="element-3154" data-mp="3tbrifna13koq">
                <div className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-xl border border-[#FF0000]/30 bg-[#FF0000]/10" data-id="element-3155" data-mp="27auwkdyn9rg">
                  <Shield className="h-5 w-5 text-[#FF0000]" data-id="element-3156" data-mp="r6s3d6yk5s3" />
                </div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500" data-id="element-3157" data-mp="3rvndoajgu6mq">
                  {prompt.eyebrow}
                </p>
                <h2 id="upgrade-modal-title" className="mt-2 text-2xl font-black uppercase tracking-tight text-white" data-id="element-3158" data-mp="3mzf4ddry97nh">
                  {prompt.title}
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-gray-400" data-id="element-3159" data-mp="ei22wv10owyb">
                  {prompt.body}
                </p>

                {prompt.primaryAction === 'single_scan' && <PaymentNotice className="mt-6" data-id="element-3160" data-mp="5pqgrlcxrvo5" />}
                <div className="mt-7 flex flex-col gap-3 sm:flex-row" data-id="element-3161" data-mp="5ne1jfykbzes">
                  <button type="button" autoFocus onClick={handlePrimary} disabled={pendingPlan !== null} className="flex-1 rounded-xl bg-[#FF0000] px-5 py-4 text-sm font-black uppercase tracking-wider text-white transition-colors duration-200 ease-out hover:bg-[#D90000] disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#050505]" data-id="element-3162" data-mp="1286afdhbtgul">
                    {pendingPlan ? 'Processing…' : prompt.primaryLabel}
                  </button>
                  <button type="button" onClick={() => setShowPlans(true)} className="flex-1 rounded-xl border border-gray-700 bg-transparent px-5 py-4 text-sm font-black uppercase tracking-wider text-white transition-colors duration-200 ease-out hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#050505]" data-id="element-3163" data-mp="2z0ftiu0uvpbe">
                    View plans
                  </button>
                </div>

                <p className="mt-5 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wide text-gray-600" data-id="element-3164" data-mp="2xofmfj34mzgy">
                  <Lock size={10} aria-hidden="true" data-id="element-3165" data-mp="2sqe5b3p9ha84" /> Secure checkout · Cancel
                  anytime
                </p>
              </div>}
          </motion.div>

          <CheckoutAuthPrompt plan={authPromptPlan} onClose={dismissAuthPrompt} data-id="element-3166" data-mp="32n8zxbieyqh4" />
        </div>}
    </AnimatePresence>;
}